import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { CallHistoryCall, CallHistorySearchParams } from '../call-history-params.interface';
import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import { GetCallHistoryAction, SetCallHistoryParamsAction } from '../call-history.actions';
import * as moment from 'moment';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  getCallHistoryCallsSelector,
  getCallHistoryPageSelector,
  getCallHistoryTotalSelector,
  initialState
} from '../call-history.reducer';

import {ContactsService} from '../../contacts/shared/services/contacts.service';
import {CallHistoryService} from '../call-history.service';
import {getLimitsSelector} from '../../shared/limits/limits.reducer';
import {AccountLimits} from '../../account/account.model';
import {UserSimplified} from '../../contacts/shared/models/Contact';
import {DatePickerService} from "../../shared/services/date.picker.service";
import {DateFilterEvent} from "../../shared/date-filter/date-filter.interface";
import {DateFilterComponent} from "../../shared/date-filter/date-filter.component";


@Component({
  selector: 'cc-call-history-list',
  templateUrl: './call-history-list.component.html',
  styleUrls: ['./call-history-list.component.scss']
})
export class CallHistoryListComponent implements OnInit, OnDestroy {
  public displayedColumns = ['direction', 'contactName', 'callTime', 'agentName', 'callLength', 'appType'];
  public dataSource: MatTableDataSource<CallHistoryCall>;
  public filters: CallHistorySearchParams = {};
  public msisdns: any;
  public users: UserSimplified[];
  public basicUsers: UserSimplified[];
  public utcOffset = moment().utcOffset();
  public startDate: string | moment.Moment;
  public endDate: string | moment.Moment;
  public maxDate: string | moment.Moment;
  public minDate: string | moment.Moment;
  public opens: string;
  public pageSizeOptions = [10, 20, 100];
  public phoneNumbersIds: number[];
  public allNumbersSelected = false;
  public allUsersSelected = false;
  public datePickerOpened = false;
  public callsHistoryLimit = 0;
  public initialFilters: CallHistorySearchParams = initialState.params;
  public keepFilters = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('openCalendar') iconCalendar: ElementRef;
  public total: number;

  constructor(
    private route: ActivatedRoute,
    private store: Store<IState>,
    private callHistoryService: CallHistoryService,
    public contactsService: ContactsService,
    public datePickerService: DatePickerService,
    private location: Location
  ) {}

  ngOnInit() {
    this.maxDate = moment().endOf('day').add(this.utcOffset / 60, 'hours').toISOString();
    this.startDate = this.toHour(moment().subtract(7, 'day').startOf('day').add(this.utcOffset / 60, 'hours'), this.utcOffset);
    this.endDate = this.toHour(moment().endOf('day').add(this.utcOffset / 60, 'hours'), this.utcOffset);
    this.keepFilters = this.route.snapshot.queryParamMap.get('keepFilters') === 'true';
    this.msisdns = this.route.snapshot.data['msisdns'];
    this.phoneNumbersIds = this.msisdns.map(number => number.id);
    this.basicUsers = this.route.snapshot.data['users'];
    this.users = this.basicUsers.filter(user => user.id === null).length
      ? this.basicUsers
      : [...this.basicUsers, { id: CallHistoryService.UNASSIGNED_USER_ID, name: 'UNASSIGNED' }];
    this.dataSource = new MatTableDataSource([]);
    this.dataSource.data = this.route.snapshot.data['callHistory'].data;
    this.dataSource.sort = this.sort;
    this.total = this.route.snapshot.data['callHistory'].total;
    this.initialFilters = {
      sortBy: 'callTime',
      order: 'DESC',
      dateFrom: moment()
        .startOf('day')
        .subtract(7, 'days')
        .toISOString(),
      dateTo: moment()
        .toISOString(),
      pager: {
        pageIndex: 0,
        pageSize: 10,
        previousPageIndex: 0
      }
    };

    this.store.select(getCallHistoryCallsSelector).subscribe((calls: CallHistoryCall[]) => {
      this.dataSource.data = calls;
    });

    this.store.select(getCallHistoryTotalSelector).subscribe((total: number) => {
      this.total = total;
      this.paginator.length = total;
    });

    this.store.select(getCallHistoryPageSelector).subscribe(params => {
      this.filters = params;
      if (this.filters && this.filters.pager) {
        const { pageSize, pageIndex } = this.filters.pager || ({} as any);
        this.paginator.pageSize = pageSize;
        this.paginator.pageIndex = pageIndex;
      }
    });

    this.store.select(getLimitsSelector).subscribe((limits: AccountLimits) => {
      this.callsHistoryLimit = limits.callsHistoryLimit;
      this.minDate = this.contactsService.calculateMinDateByLimit(limits.callsHistoryLimit, this.utcOffset);
    });

    this.datePickerService.onTogglePicker.subscribe((open: { open: string; show: boolean }) => {
      this.opens = open && open.open ? open.open : 'left';
      this.datePickerOpened = open && open.show;
    });
    if (!this.keepFilters) {
      this.filters = this.initialFilters;
      this.store.dispatch(new GetCallHistoryAction(this.filters));
    }
  }

  toHour(date, utcOffset) {
    return date.toISOString();
  }

  toggleAllNumbersSelect() {
    this.allNumbersSelected = !this.allNumbersSelected;
  }

  toggleAllUsersSelect() {
    this.allUsersSelected = !this.allUsersSelected;
  }

  addFilter(type, value) {
    this.callHistoryService.addFilter(type, value, this.callHistoryService.getPagerIndexReset(this.filters.pager));
  }

  addDisplayParams(event) {
    this.filters.pager = {
      ...this.filters.pager,
      ...event
    };
    this.store.dispatch(new GetCallHistoryAction(this.filters));
  }

  ngOnDestroy() {
    if (!this.keepFilters) {
      this.store.dispatch(new SetCallHistoryParamsAction(this.initialFilters));
    }
  }

  callsTableDetailsClicked() {
    this.location.replaceState('/call-history?keepFilters=true');
    this.keepFilters = true;
  }

  dateFilterChange($event: DateFilterEvent) {
    this.startDate = $event.dateFrom.isAfter(this.minDate)  ? $event.dateFrom : this.minDate;
    this.endDate = $event.dateTo;
    this.filters.dateFrom = $event.dateFrom.toISOString();
    this.filters.dateTo = $event.dateTo.toISOString();
    this.store.dispatch(new GetCallHistoryAction(this.filters));
  }
}
